import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Popover, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const INDIGO = "#4338ca";

/**
 * "Duplicate…" for the open tab: the platforms ticked here reuse this
 * platform's video (Step 3's Duplicate). Each other platform of the WGID has
 * its own box (user request 2026-10-07): ticked by default unless it has a
 * saved edit of its own. `onApply({ platforms })` returns false when nothing
 * ran (the tab could not be saved first).
 */
export default function ApplyToAllControl({ item, plan, needsSave, onApply, onPanelChange }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => onPanelChange(open), [open, onPanelChange]);
  useEffect(() => () => onPanelChange(false), [onPanelChange]);

  const current = plan(item, picked);
  const replacing = current.targets.filter((i) => current.edited.includes(i));

  const openPanel = () => {
    setPicked(new Set(plan(item).defaults.map((i) => i.platform)));
    setOpen(true);
  };

  const toggle = (platform) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });

  const apply = async () => {
    if ((await onApply({ platforms: current.targets.map((i) => i.platform) })) !== false) setOpen(false);
  };

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        variant="outlined"
        startIcon={<ContentCopyIcon sx={{ fontSize: "13px !important" }} />}
        onClick={openPanel}
        disabled={current.inScope.length === 0}
        title={current.inScope.length === 0 ? `${item.wgid} has no other platforms to duplicate to` : undefined}
        data-testid="batch-duplicate"
        sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, minHeight: 26, height: 26, py: 0, px: 1, fontSize: 11.5, borderColor: alpha(INDIGO, 0.4), color: INDIGO }}
      >
        Duplicate…
      </Button>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 1, p: 2, width: 420, maxWidth: "calc(100vw - 32px)", borderRadius: 2 } }}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Duplicate {item.platform} to other platforms of {item.wgid}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          The ticked platforms use this {item.platform} video exactly — the same as choosing <strong>Duplicate → {item.platform}</strong> in Step 3.
          One render, and later changes to {item.platform} carry over automatically.
        </Typography>

        <Box role="group" aria-label="Platforms to duplicate to" sx={{ display: "flex", flexDirection: "column" }}>
          {current.candidates.map((i) => {
            const own = current.edited.includes(i);
            return (
              <FormControlLabel
                key={i.platform}
                sx={{ mr: 0, alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.5 } }}
                control={
                  <Checkbox
                    size="small"
                    checked={picked.has(i.platform)}
                    onChange={() => toggle(i.platform)}
                    inputProps={{ "data-testid": `batch-duplicate-${i.platform}` }}
                  />
                }
                label={
                  <Box py={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {i.platform}
                    </Typography>
                    {own && (
                      <Typography variant="caption" color={picked.has(i.platform) ? "warning.dark" : "text.secondary"}>
                        {picked.has(i.platform) ? "Its own saved edit will be replaced" : "Has its own saved edit — kept"}
                      </Typography>
                    )}
                  </Box>
                }
              />
            );
          })}
          {current.already.map((i) => (
            <FormControlLabel
              key={i.platform}
              disabled
              sx={{ mr: 0 }}
              control={<Checkbox size="small" checked />}
              label={
                <Typography variant="body2">
                  <strong>{i.platform}</strong> — already duplicates {item.platform}
                </Typography>
              }
            />
          ))}
        </Box>

        <Box role="status" sx={{ mt: 1, px: 1.25, py: 1, borderRadius: 1.5, bgcolor: alpha(INDIGO, 0.05), border: `1px solid ${alpha(INDIGO, 0.15)}` }}>
          <Typography variant="body2">
            {current.targets.length > 0 ? (
              <strong>
                {current.targets.map((i) => i.platform).join(", ")} will duplicate {item.platform}.
              </strong>
            ) : (
              <strong>Tick a platform to duplicate to.</strong>
            )}
            {replacing.length > 0 && ` Replaces the saved edit of ${replacing.map((i) => i.platform).join(", ")}.`}
            {needsSave && current.targets.length > 0 && ` This tab is saved first.`}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={apply}
            disabled={current.targets.length === 0}
            startIcon={<ContentCopyIcon sx={{ fontSize: "16px !important" }} />}
            data-testid="batch-duplicate-run"
            sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, #312e81, ${INDIGO})` }}
          >
            {needsSave ? "Save & duplicate" : "Duplicate"}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
